import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Store, Sparkles, MessageCircle, HelpCircle, ArrowRight, Check, X, Star,
  Zap, ShieldCheck, Gem, BadgeCheck, Link2, TrendingUp, Users, DollarSign, Wallet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { billing, creator } from '../mock';
import { usd } from '../lib/format';

const features = [
  {
    icon: Sparkles,
    title: 'AI Sales Concierge',
    desc: 'A tireless closer that answers questions, handles objections, and recommends the perfect offer — 24/7.',
    tag: 'Converts while you sleep',
  },
  {
    icon: MessageCircle,
    title: 'Auto-DM Engine',
    desc: 'Turn every comment into a conversation. Trigger keyword DMs on Instagram & TikTok that funnel fans to your store.',
    tag: 'Up to 40% more replies',
  },
  {
    icon: HelpCircle,
    title: 'Quiz Funnels',
    desc: 'Interactive assessments that qualify visitors and route them to the exact product they’re ready to buy.',
    tag: 'Higher intent, higher AOV',
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const goBuild = () => navigate('/onboarding');
  const importAndBuild = (e) => { e.preventDefault(); navigate('/onboarding'); };

  return (
    <div className="luxe-bg min-h-screen w-full overflow-hidden">
      {/* Nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Store className="h-4 w-4" /></span>
          <span className="font-display text-xl font-bold text-white">BioCloser</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/store"><Button variant="ghost" size="sm" className="rounded-full text-white/80 hover:bg-white/10 hover:text-white">See a live store</Button></Link>
          <Button onClick={goBuild} size="sm" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">Start free</Button>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-10 text-center sm:pt-16">
        <div className="animate-fade-up mx-auto mb-5 flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/70">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> The AI-native creator store
        </div>
        <h1 className="animate-fade-up font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl" data-testid="hero-headline">
          Replace Linktree + Manychat{' '}<br className="hidden sm:block" />
          with one link that <span className="text-gradient-lime">closes 24/7</span>
        </h1>
        <p className="animate-fade-up mx-auto mt-5 max-w-xl text-base text-white/70 sm:text-lg" data-testid="hero-subtext">
          BioCloser replies in DMs, qualifies leads and sends checkout links while you sleep.
        </p>
        <div className="animate-fade-up mt-7 flex flex-col items-center justify-center gap-3">
          <form onSubmit={importAndBuild} className="glass flex w-full max-w-md items-center gap-2 rounded-2xl p-2">
            <div className="relative flex-1">
              <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste your bio link (linktr.ee/you)"
                className="h-11 rounded-xl border-white/10 bg-white/[0.04] pl-9 text-sm focus-visible:ring-primary"
              />
            </div>
            <Button type="submit" className="h-11 shrink-0 rounded-xl bg-primary px-5 font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
              Auto-Import
            </Button>
          </form>
          <span className="flex items-center gap-1.5 text-sm text-white/60"><ShieldCheck className="h-4 w-4 text-primary" /> Instantly generates your store · No card required · 0% fees on Pro</span>
        </div>

        {/* Hero preview card */}
        <div className="animate-fade-up mx-auto mt-14 max-w-sm">
          <div className="glass relative rounded-[2rem] p-4 animate-floaty">
            <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[3rem] bg-primary/10 blur-3xl" />
            <div className="flex flex-col items-center">
              <img src={creator.avatar} alt={creator.name} className="h-16 w-16 rounded-full border-2 border-white/10 object-cover" />
              <div className="mt-2 flex items-center gap-1.5">
                <span className="font-display text-base font-bold text-white">{creator.name}</span>
                <BadgeCheck className="h-4 w-4 fill-primary text-primary-foreground" />
              </div>
              <span className="text-xs text-primary">{creator.tagline}</span>
            </div>
            <div className="mt-4 space-y-2">
              {['1-on-1 Growth Audit · $150', '7-Figure Offer Masterclass · $299', 'Strategy Workbook · $49'].map((t) => (
                <div key={t} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-2.5 text-left text-sm text-white/85">
                  <span className="h-8 w-8 rounded-lg bg-primary/15" />
                  <span className="flex-1 truncate">{t}</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-primary/[0.08] p-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/20"><Sparkles className="h-3.5 w-3.5 text-primary" /></span>
              <span className="text-xs text-white/80">AI Concierge: “Want me to find your perfect offer?”</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Not just a link. A revenue engine.</h2>
          <p className="mt-3 text-white/60">Everything you need to sell high-ticket offers on autopilot.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {features.map((f, i) => (
            <div key={f.title} className="group glass animate-fade-up rounded-2xl p-6 transition-luxe hover:-translate-y-1.5 hover:border-primary/30" style={{ animationDelay: `${i * 90}ms` }}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 transition-luxe group-hover:scale-110">
                <f.icon className="h-6 w-6 text-primary" />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{f.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] px-3 py-1 text-xs font-medium text-primary">
                <Zap className="h-3 w-3" /> {f.tag}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Dashboard preview */}
      <section className="mx-auto max-w-6xl px-5 py-16" data-testid="dashboard-preview">
        <div className="mb-10 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/70"><TrendingUp className="h-3.5 w-3.5 text-primary" /> Your dashboard, working overnight</span>
          <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">Watch revenue close while you sleep</h2>
          <p className="mt-3 text-white/60">Real numbers from creators running their whole funnel on one link.</p>
        </div>

        <div className="glass relative mx-auto max-w-4xl overflow-hidden rounded-[2rem] p-5 sm:p-7">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />

          {/* header row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Store className="h-4 w-4" /></span>
              <span className="font-display text-sm font-bold text-white">Creator Dashboard</span>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-primary/[0.1] px-3 py-1 text-xs font-medium text-primary"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" /> Live</span>
          </div>

          {/* stat tiles */}
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { icon: DollarSign, label: 'Total handled', value: '$318,420', sub: '+18.2% this month' },
              { icon: Users, label: 'Leads qualified', value: '4,912', sub: '+642 this week' },
              { icon: MessageCircle, label: 'DMs closed by AI', value: '1,204', sub: '68% close rate' },
              { icon: Wallet, label: 'Kept on Pro (0% fees)', value: '$28,657', sub: 'vs 9% on Free' },
            ].map((s, i) => (
              <div key={s.label} className="animate-fade-up rounded-2xl border border-white/10 bg-white/[0.03] p-4" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15"><s.icon className="h-4.5 w-4.5 text-primary" /></span>
                <div className="mt-3 font-display text-2xl font-extrabold tracking-tight text-white">{s.value}</div>
                <div className="mt-0.5 text-xs text-white/55">{s.label}</div>
                <div className="mt-1.5 text-[11px] font-medium text-primary">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* chart + activity */}
          <div className="mt-4 grid gap-3 lg:grid-cols-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 lg:col-span-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Revenue handled</span>
                <span className="text-xs text-white/50">Last 12 weeks</span>
              </div>
              <div className="mt-6 flex h-36 items-end gap-2">
                {[38, 46, 41, 58, 52, 67, 74, 63, 82, 78, 91, 100].map((h, i) => (
                  <div key={i} className="group relative flex-1 rounded-t-md bg-primary/25 transition-luxe hover:bg-primary" style={{ height: `${h}%` }}>
                    <span className="absolute inset-x-0 -top-px h-1 rounded-t-md bg-primary/70" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 lg:col-span-2">
              <span className="text-sm font-semibold text-white">AI closed just now</span>
              <div className="mt-4 space-y-3">
                {[
                  { name: 'Jordan M.', product: '7-Figure Masterclass', amt: '$299' },
                  { name: 'Priya S.', product: 'Growth Audit Call', amt: '$150' },
                  { name: 'Leo K.', product: 'Inner Circle', amt: '$97' },
                ].map((r) => (
                  <div key={r.name} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">{r.name[0]}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-white/90">{r.name}</div>
                      <div className="truncate text-xs text-white/50">{r.product}</div>
                    </div>
                    <span className="text-sm font-bold text-primary">{r.amt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-4xl px-5 py-8">
        <div className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Simple, creator-friendly pricing</h2>
          <p className="mt-3 text-white/60">Start free. Upgrade when you’re ready to keep 100% of your sales.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {billing.tiers.map((t) => (
            <div key={t.id} className={`relative overflow-hidden rounded-3xl p-7 ${t.id === 'pro' ? 'border border-primary/40 bg-primary/[0.06]' : 'glass'}`}>
              {t.id === 'pro' && <span className="absolute right-5 top-5 flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold text-primary-foreground"><Gem className="h-3 w-3" /> BEST VALUE</span>}
              <h3 className="font-display text-xl font-bold text-white">{t.name}</h3>
              <p className="mt-0.5 text-sm text-white/55">{t.tagline}</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-white">{t.price === 0 ? 'Free' : usd(t.price)}</span>
                {t.price > 0 && <span className="mb-1.5 text-white/55">/mo</span>}
              </div>
              <p className="mt-1 text-sm font-medium text-primary">{t.fee}</p>
              <ul className="mt-5 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/85"><Check className="h-4 w-4 shrink-0 text-primary" /> {f}</li>
                ))}
                {t.locked.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/35"><X className="h-4 w-4 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Button onClick={goBuild} className={`mt-6 w-full rounded-xl font-semibold ${t.id === 'pro' ? 'bg-primary text-primary-foreground hover:bg-primary/90 glow-lime' : 'bg-white/10 text-white hover:bg-white/15'}`}>
                {t.id === 'pro' ? 'Start Pro trial' : 'Build for free'}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-4xl px-5 py-16">
        <div className="glass relative overflow-hidden rounded-[2rem] p-10 text-center">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/15 blur-3xl" />
          <div className="mx-auto mb-4 flex w-fit items-center gap-1 text-primary">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary" />)}
          </div>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Your bio link should be making you money.</h2>
          <p className="mx-auto mt-3 max-w-md text-white/65">Import your existing links in 5 seconds and let AI do the selling.</p>
          <Button onClick={goBuild} className="mt-6 h-13 rounded-xl bg-primary px-7 py-6 text-base font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
            Build Your Free Storefront <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-white/40">
        © 2025 BioCloser · biocloser.com · Built for creators
      </footer>
    </div>
  );
}
